import {
	aiWorkflowsAllowedEmails,
	App,
	AppError,
	AppMenuPoint,
	AppMenuPoints,
	AppNavMenuPointsService,
	ErrorService,
	ErrorType,
	getEnvironmentFromUrl,
	MenuPointService,
	NavVariant,
	PlanRoutes,
	Role,
	workflowsAllowedEmails,
} from '@agilox/common';
import {
	CookieOverlayComponent,
	ErrorContentComponent,
	ErrorPageComponent,
	HelpContentComponent,
	NavModule,
	ToastContainerComponent,
} from '@agilox/components';
import { OverlayComponent } from '@agilox/ui';
import { MenuPoint, MenuPointCallbackAction } from '@agilox/ui-common';
import { AsyncPipe, LowerCasePipe } from '@angular/common';
import {
	Component,
	DestroyRef,
	inject,
	OnInit,
	signal,
	ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router, RouterModule } from '@angular/router';
import { AuthService } from '@auth';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserStoreService } from '@user-store';
import { combineLatest, filter, map, Observable, shareReplay, startWith } from 'rxjs';
import { MenuPointUrl } from './general/shared/models/enums/menupoint-url.enum';

@Component({
	imports: [
		RouterModule,
		AsyncPipe,
		ErrorContentComponent,
		ErrorPageComponent,
		HelpContentComponent,
		LowerCasePipe,
		NavModule,
		OverlayComponent,
		TranslatePipe,
		ToastContainerComponent,
		CookieOverlayComponent,
	],
	selector: 'plan-root',
	templateUrl: './app.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
	protected errorService = inject(ErrorService);
	private menuPointService = inject(MenuPointService);
	public userStore = inject(UserStoreService);
	public auth = inject(AuthService);
	private translateService = inject(TranslateService);
	private appMenuPointsService = inject(AppNavMenuPointsService);
	private router = inject(Router);
	private destroyRef = inject(DestroyRef);

	private _routerEvents$: Observable<any> = this.router.events.pipe(
		takeUntilDestroyed(),
		shareReplay(1)
	);

	private role$ = this.userStore.role$.pipe(
		filter((role): role is Role => !!role),
		shareReplay(1)
	);

	private language$ = this.translateService.onLangChange.pipe(
		startWith({ lang: this.translateService.currentLang || 'en' }),
		shareReplay(1)
	);

	private roleAndLanguage$ = combineLatest([this.role$, this.language$]);

	public desktopMenuPoints$ = this.roleAndLanguage$.pipe(
		map(([role, _]) =>
			this.filterRestrictedMenuPoints(
				this.menuPointService.getMenuPointsByCategory(NavVariant.DESKTOP, role as Role)
			)
		)
	);

	public mobileMenuPoints$ = this.roleAndLanguage$.pipe(
		map(([role, _]) =>
			this.filterRestrictedMenuPoints(
				this.menuPointService.getMenuPointsByCategory(NavVariant.MOBILE, role as Role)
			)
		)
	);

	public appNavMenuPoints$ = this.roleAndLanguage$.pipe(
		map(([role, langChange]) => this.appMenuPointsService.getAppNavMenuPoints(role as Role)),
		startWith([]),
		shareReplay(1)
	);

	activeApp: AppMenuPoint | undefined = AppMenuPoints.find((app) => app.name === 'plan');
	environment: string = getEnvironmentFromUrl(PlanRoutes);
	error$: Observable<AppError | null> = this.errorService.error$;
	helpOpen = signal(false);

	/**
	 * Will get triggered when a navigation chunk fails to load because the user is offline
	 * or the chunk is not available (e.g. due to a new version of the app)
	 */
	private _navigationError$: Observable<NavigationError> = this._routerEvents$.pipe(
		takeUntilDestroyed(this.destroyRef),
		filter((event) => event instanceof NavigationError)
	);

	pageName$ = this._routerEvents$.pipe(
		filter((event) => event instanceof NavigationEnd),
		map((event) => {
			return 'p-' + event.urlAfterRedirects.replace('/', '').replace(/\//g, '-').split('?')[0];
		}),
		takeUntilDestroyed(this.destroyRef)
	);

	currentUrl$ = this._routerEvents$.pipe(
		filter((event) => event instanceof NavigationEnd),
		map((event) => event.urlAfterRedirects),
		takeUntilDestroyed(this.destroyRef)
	);

	constructor() {
		this._navigationError$.subscribe((event) => {
			if (!navigator.onLine) {
				this.errorService.setError({
					application: App.plan,
					type: ErrorType.NO_INTERNET_ERROR,
				});
			} else if (event.error.toString().includes('chunk')) {
				this.errorService.setError({
					application: App.plan,
					type: ErrorType.CHUNK_ERROR,
				});
			}
		});
	}

	ngOnInit() {
		const lang = this.userStore.currentUser()?.lang || 'en';
		this.translateService.use(lang);
	}

	navCallback(menuPoint: MenuPoint) {
		if (menuPoint.callbackAction === MenuPointCallbackAction.HELP) {
			this.helpOpen.set(true);
			return;
		}

		if (menuPoint.callbackAction === MenuPointCallbackAction.LOGOUT) {
			this.logout();
		}
	}

	onClose(): void {
		this.errorService.clearError();
	}

	onCloseHelp(): void {
		this.helpOpen.set(false);
	}

	logout() {
		this.auth.logout();
	}

	private filterRestrictedMenuPoints(menuPoints: MenuPoint[]): MenuPoint[] {
		const email = this.userStore.currentUser()?.email?.toLowerCase();
		const restrictedUrls: MenuPointUrl[] = [];
		if (!email || !aiWorkflowsAllowedEmails.includes(email)) {
			restrictedUrls.push(MenuPointUrl.AI_WORKFLOWS);
		}
		if (!email || !workflowsAllowedEmails.includes(email)) {
			restrictedUrls.push(MenuPointUrl.WORKFLOWS);
		}
		if (!restrictedUrls.length) {
			return menuPoints;
		}
		return menuPoints
			.filter((mp) => !restrictedUrls.includes(mp.url as MenuPointUrl))
			.map((mp) =>
				mp.submenus
					? {
							...mp,
							submenus: mp.submenus.filter(
								(sub) => !restrictedUrls.includes(sub.url as MenuPointUrl)
							),
						}
					: mp
			);
	}
}
